import { Box } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import React from 'react';

import { Channel } from 'app/constants/channel';
import { MidTuple } from 'app/model/mid-tuple';
import { WithEsId } from 'app/type/with-es-id';

interface TupleCellProps {
    params: GridRenderCellParams<WithEsId<MidTuple>>;
    highlightField?: string;
    highlightPattern?: RegExp;
    highlightChannel?: Channel;
}

export const TupleCell: React.FunctionComponent<TupleCellProps> = (props) => {
    const valueNode = makeHighlight(props);

    return <>{valueNode}</>;
};

const makeHighlight = (props: TupleCellProps): React.ReactNode => {
    const value = (props.params.formattedValue as string | undefined) ?? '';

    if ('channel' === props.params.field && props.highlightChannel) {
        // for channel, we highlight entire thing, it either matches the selected value or not.
        const bg = props.params.value === props.highlightChannel ? 'success.' : 'warning.';

        return <Box sx={{ bgcolor: `${bg}light`, color: `${bg}contrastText` }}>{props.params.value}</Box>;
    }

    if (!props.highlightPattern || props.highlightField !== props.params.field) {
        // if we have no highlight pattern just, or it is not for current field display value as is
        return <span className='text-pre'>{value}</span>;
    }

    const children: React.ReactNode[] = [];
    let currentIndex = 0;

    let matches: RegExpExecArray | null;

    // walk all matches
    while ((matches = props.highlightPattern.exec(value))) {
        // push data in front of match as is
        children.push(
            <span key={children.length} className='text-pre'>
                {value.substring(currentIndex, matches.index)}
            </span>,
        );
        // push match as a highlighted box
        children.push(...highlightSubGroup((matches as unknown as { indices: number[][] }).indices, value));
        currentIndex = props.highlightPattern.lastIndex;
    }

    // push any remaining data as is
    children.push(
        <span key={children.length} className='text-pre'>
            {value.substring(currentIndex)}
        </span>,
    );

    return children;
};

const highlightSubGroup = (indices: number[][], value: string): React.ReactNode[] => {
    if (1 === indices.length) {
        // no groups in pattern just return main match
        return [
            <Box
                sx={{
                    bgcolor: 'warning.light',
                    color: 'warning.contrastText',
                    borderRight: 1,
                    borderColor: 'warning.contrastText',
                }}
                className='text-pre'
                key={indices[0][0]}
            >
                {value.substring(indices[0][0], indices[0][1])}
            </Box>,
        ];
    }

    const children: React.ReactNode[] = [];
    // before first group
    children.push(
        <Box
            sx={{
                bgcolor: 'info.light',
                color: 'warning.contrastText',
                borderRight: 1,
                borderColor: 'warning.contrastText',
            }}
            className='text-pre'
            key={indices[0][0]}
        >
            {value.substring(indices[0][0], indices[1][0])}
        </Box>,
    );

    // first group
    children.push(
        <Box
            sx={{
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
                borderRight: 1,
                borderColor: 'warning.contrastText',
            }}
            className='text-pre'
            key={indices[1][0]}
        >
            {value.substring(indices[1][0], indices[1][1])}
        </Box>,
    );

    // after first group
    children.push(
        <Box
            sx={{
                bgcolor: 'info.light',
                color: 'warning.contrastText',
                borderRight: 1,
                borderColor: 'warning.contrastText',
            }}
            className='text-pre'
            key={indices[1][1]}
        >
            {value.substring(indices[1][1], indices[0][1])}
        </Box>,
    );

    return children;
};
